import { onlineManager } from "@tanstack/react-query";
import { useEffect, useSyncExternalStore } from "react";

class InternetStatus {
  private currentIsOnline: boolean;
  private listeners: ((status: boolean) => void)[];

  constructor() {
    // Use navigator.onLine for web
    this.currentIsOnline = navigator.onLine;
    this.listeners = [];

    this.subscribeToWebNetworkEvents();
  }

  private subscribeToWebNetworkEvents = () => {
    // Listen to browser online/offline events
    const handleOnline = () => {
      this.updateOnlineStatus(true);
    };

    const handleOffline = () => {
      this.updateOnlineStatus(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Also check initially
    this.updateOnlineStatus(navigator.onLine);

    // Cleanup function would be called if this class had a destroy method
    // For now, these listeners will persist for the app lifecycle
  };

  private updateOnlineStatus = (isOnline: boolean) => {
    if (this.currentIsOnline !== isOnline) {
      this.currentIsOnline = isOnline;

      // Notify all listeners
      this.listeners.forEach((listener) => listener(isOnline));

      // Also update React Query
      onlineManager.setOnline(isOnline);
      console.log(`::WebNetworkListener:: isOnline: ${isOnline}`);
    }
  };

  // Public interface

  public getCurrentIsOnline = () => {
    return this.currentIsOnline;
  };

  public subscribe = (listener: (status: boolean) => void) => {
    this.listeners.push(listener);
  };

  public unsubscribe = (listener: (status: boolean) => void) => {
    this.listeners = this.listeners.filter((l) => l !== listener);
  };
}

export const internetStatus = new InternetStatus();

const useInternetStatus = () => {
  // Subscribe via useSyncExternalStore so the snapshot is always read at
  // subscription time. This removes the render<->subscribe race that the old
  // effect-based re-sync handled, with no setState in an effect body.
  return useSyncExternalStore(
    (onStoreChange) => {
      internetStatus.subscribe(onStoreChange);
      return () => internetStatus.unsubscribe(onStoreChange);
    },
    () => internetStatus.getCurrentIsOnline(),
    () => internetStatus.getCurrentIsOnline(),
  );
};

export const ReactQueryOnlineManager = () => {
  useEffect(() => {
    const unsubscribe = onlineManager.subscribe((isOnline) => {
      console.log(`::ReactQueryOnlineManager:: isOnline: ${isOnline}`);
    });

    return () => unsubscribe();
  }, []);

  return null;
};

export default useInternetStatus;
